import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tracksApi } from '../api/tracks';
import { ArrowLeft, Play, Clock, Headphones, Pause } from 'lucide-react';
import { usePlayerStore } from '../store/playerStore';
import PlayingAnimation from '../components/PlayingAnimation';

export default function TrackDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const playTrack = usePlayerStore((state) => state.playTrack);
  const pause = usePlayerStore((state) => state.pause);
  const resume = usePlayerStore((state) => state.resume);
  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const isPlaying = usePlayerStore((state) => state.isPlaying);

  const { data: track, isLoading } = useQuery({
    queryKey: ['track', id],
    queryFn: () => tracksApi.getOne(id!),
    enabled: !!id,
  });

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isCurrentTrack = currentTrack?._id === track?._id;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;

  const handlePlayPause = () => {
    if (!track) return;
    if (isCurrentTrack) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      playTrack(track);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="mt-4 text-gray-400">{t('trackDetail.loading', 'Loading...')}</p>
      </div>
    );
  }

  if (!track) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🎵</div>
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('trackDetail.notFound', 'Track not found')}
        </h3>
        <Link to="/tracks" className="text-primary-400 hover:text-primary-300 transition-colors">
          ← {t('trackDetail.backToTracks', 'Back to Tracks')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tracks"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={20} />
        {t('trackDetail.backToTracks', 'Back to Tracks')}
      </Link>

      {/* Track Card */}
      <div className="bg-gradient-to-br from-primary-600/30 via-secondary-600/20 to-transparent backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/10 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Track Image */}
          <div className="relative flex-shrink-0">
            <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-6xl overflow-hidden shadow-2xl">
              {track.image ? (
                <img src={track.image} alt={track.name} className="w-full h-full object-cover" />
              ) : (
                <span>🎵</span>
              )}
            </div>
            {isCurrentlyPlaying && (
              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm rounded-full p-2">
                <PlayingAnimation />
              </div>
            )}
          </div>

          {/* Track Info */}
          <div className="flex-1 text-center sm:text-left">
            <p className="text-primary-400 text-sm font-medium mb-2 uppercase tracking-wider">
              {t('trackDetail.track', 'Track')}
            </p>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {track.name}
            </h1>

            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-6 text-gray-300 mb-6">
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-gray-400" />
                <span>{formatDuration(track.duration)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Headphones size={18} className="text-gray-400" />
                <span>{track.plays.toLocaleString()} {t('trackDetail.plays', 'plays')}</span>
              </div>
            </div>

            {/* Play Button */}
            <button
              onClick={handlePlayPause}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-full font-bold transition-all flex items-center gap-3 shadow-lg hover:shadow-glow mx-auto sm:mx-0"
            >
              {isCurrentlyPlaying ? (
                <>
                  <Pause size={20} fill="currentColor" />
                  {t('trackDetail.pause', 'Pause')}
                </>
              ) : (
                <>
                  <Play size={20} fill="currentColor" />
                  {t('trackDetail.play', 'Play')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Description */}
      {track.description && (
        <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-6">
          <h2 className="text-xl font-bold text-white mb-3">
            {t('trackDetail.about', 'About')}
          </h2>
          <p className="text-gray-300 whitespace-pre-wrap">{track.description}</p>
        </div>
      )}

      {/* Track Stats */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-6">
        <h2 className="text-xl font-bold text-white mb-4">
          {t('trackDetail.info', 'Track Info')}
        </h2>
        <dl className="grid grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-xl p-4">
            <dt className="text-gray-400 text-sm mb-1">{t('trackDetail.duration', 'Duration')}</dt>
            <dd className="text-white font-bold text-lg">{formatDuration(track.duration)}</dd>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <dt className="text-gray-400 text-sm mb-1">{t('trackDetail.totalPlays', 'Total Plays')}</dt>
            <dd className="text-white font-bold text-lg">{track.plays.toLocaleString()}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
